import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaReply, FaShare, FaStar, FaThumbtack, FaHeart, FaTrash, FaCheckSquare, FaShareAlt } from 'react-icons/fa';

// 🎨 CONTEXT MENU: WhatsApp-style message context menu
const MessageContextMenu = ({ 
  message, 
  isVisible, 
  position, 
  onClose, 
  onReply, 
  onForward, 
  onStar, 
  onPin, 
  onAddToFavorites, 
  onDelete, 
  onSelect, 
  onShare,
  onReact 
}) => {
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') onClose();
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const reactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  return (
    <motion.div
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      className="fixed z-50 bg-gray-900 bg-opacity-95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700 min-w-[200px]"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%)'
      }}
    >
      {/* Menu Items */}
      <div className="py-1">
        <button
          onClick={() => { onReply(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaReply className="text-gray-400" size={16} />
          <span className="text-sm">Reply</span>
        </button>
        
        <button
          onClick={() => { onForward(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaShare className="text-gray-400" size={16} />
          <span className="text-sm">Forward</span>
        </button>
        
        <button
          onClick={() => { onStar(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaStar className="text-gray-400" size={16} />
          <span className="text-sm">Star</span>
        </button>
        
        <button
          onClick={() => { onPin(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaThumbtack className="text-gray-400" size={16} />
          <span className="text-sm">Pin</span>
        </button>
        
        <button
          onClick={() => { onAddToFavorites(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaHeart className="text-gray-400" size={16} />
          <span className="text-sm">Add to favorites</span>
        </button>
        
        <div className="border-t border-gray-600 my-1"></div>
        
        <button
          onClick={() => { onDelete(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:bg-gray-700 transition-colors duration-150"
        >
          <FaTrash className="text-red-400" size={16} />
          <span className="text-sm">Delete for me</span>
        </button>
        
        <button
          onClick={() => { onSelect(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaCheckSquare className="text-gray-400" size={16} />
          <span className="text-sm">Select</span>
        </button>
        
        <button
          onClick={() => { onShare(message); onClose(); }}
          className="w-full flex items-center gap-3 px-4 py-2 text-white hover:bg-gray-700 transition-colors duration-150"
        >
          <FaShareAlt className="text-gray-400" size={16} />
          <span className="text-sm">Share</span>
        </button>
      </div>
      
      {/* Reactions */}
      <div className="border-t border-gray-600 pt-2 pb-1">
        <div className="flex items-center justify-center gap-2 px-2">
          {reactions.map((emoji, index) => (
            <button
              key={index}
              onClick={() => { onReact(message, emoji); onClose(); }}
              className="text-xl hover:scale-110 transition-transform duration-150 p-1 rounded"
            >
              {emoji}
            </button>
          ))}
          <button
            onClick={() => { /* Open full emoji picker */ onClose(); }}
            className="text-gray-400 hover:text-white transition-colors duration-150 p-1 rounded"
          >
            +
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default MessageContextMenu;
